import { FlexComponentMap, FlexComponentMapOptions } from "components/flexFramework/FlexComponents/typings";
import { BlossomComponent } from "../domain/BlossomComponent";

/**
 * Class that knows how to properly code split a list of Blossom Components
 */
export class BlossomCodeSplitter {
  private readonly componentMap: FlexComponentMap;
  private readonly components: BlossomComponent[];

  public constructor(componentMap: FlexComponentMap, components: BlossomComponent[]) {
    this.componentMap = componentMap;
    this.components = components;
  }

  /**
   * Returns a list of webpack react components from the Flex Component Map that we plan to use.
   * If no components are present, then we return all the values from the Flex Component Map.
   */
  public getWebpackReactComponents = () => {
    // Return all components for server-side
    if (!this.components || this.components.length === 0) {
      return [...this.componentMap.values()];
    }

    const reducer = (acc: Set<FlexComponentMapOptions>, component: BlossomComponent) => {
      if (!component || component.name === undefined) {
        return acc;
      }

      const reactComponent = component.getReactComponent();
      if (reactComponent) {
        acc.add(reactComponent);
      }

      if (component.getBlossomComponents()) {
        component.getBlossomComponents().forEach((module) => {
          reducer(acc, module);
        });
      }

      return acc;
    };

    return [
      ...this.components
        .filter((component) => component.shouldRender())
        .reduce(reducer, new Set<FlexComponentMapOptions>()),
    ];
  };

  /**
   * Uses getWebpackReactComponents() to get webpack react components and loads them.
   */
  public codeSplitAllReactComponents = () => {
    const componentsToCodeSplit = this.getWebpackReactComponents()
      .filter((module) => module && module.codeSplit && typeof module.codeSplit.loadRoute === "function")
      .map((module) => module && module.codeSplit.loadRoute());

    return Promise.all(componentsToCodeSplit);
  };
}
